import { useState, useRef, useEffect } from "react";
import { Play, Volume2, VolumeX } from "lucide-react";

export default function HockeyReel({ src, poster, className = "" }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!videoRef.current) return;
    // Lazy autoplay when in view (muted so mobile browsers allow it)
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && videoRef.current && videoRef.current.paused) {
            videoRef.current.play().then(() => {
              setPlaying(true);
              setHasStarted(true);
            }).catch(() => { /* ignore autoplay block */ });
          } else if (!e.isIntersecting && videoRef.current && !videoRef.current.paused) {
            videoRef.current.pause();
            setPlaying(false);
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(videoRef.current);
    return () => io.disconnect();
  }, []);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play().then(() => {
        setPlaying(true);
        setHasStarted(true);
      }).catch(() => {
        // Autoplay/play was blocked — leave overlay showing
      });
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    setMuted((m) => !m);
  };

  return (
    <div
      data-testid="hockey-reel"
      className={`relative bg-black overflow-hidden ${className}`}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        muted={muted}
        loop
        playsInline
        preload="metadata"
        onPlay={() => { setPlaying(true); setHasStarted(true); }}
        onPause={() => setPlaying(false)}
        onClick={togglePlay}
        className="w-full h-full object-cover cursor-pointer"
        data-testid="hockey-reel-video"
      />
      {/* Big centered play button — shown when not playing (before start OR after pause) */}
      {!playing && (
        <button
          onClick={togglePlay}
          aria-label="Play hockey reel"
          data-testid="hockey-reel-play-btn"
          className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/25 transition-colors pointer-events-auto"
        >
          <div className="w-20 h-20 rounded-full bg-[#F7931E] flex items-center justify-center shadow-2xl">
            <Play size={36} className="text-black ml-1.5" fill="currentColor" />
          </div>
        </button>
      )}
      {/* Mute toggle — shown once the reel has started */}
      {hasStarted && (
        <button
          onClick={toggleMute}
          aria-label={muted ? "Unmute reel" : "Mute reel"}
          data-testid="hockey-reel-mute-btn"
          className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors z-10"
        >
          {muted ? <VolumeX size={18} className="text-white" /> : <Volume2 size={18} className="text-white" />}
        </button>
      )}
    </div>
  );
}
